import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, MessageCircle, Ruler, DoorOpen, AppWindow, ArrowRight } from "lucide-react";
import { STORE, waLink, formatGs } from "../data/store";
import { productPageUrl } from "./ShareButton";

export const PAINT_TYPES = [
  { key: "latex_int", label: "Látex interior", yieldPerL: 10, match: ["Látex Interior"], sector: "inmobiliario" },
  { key: "latex_ext", label: "Látex exterior", yieldPerL: 8, match: ["Látex Exterior"], sector: "inmobiliario" },
  { key: "esmalte", label: "Esmalte sintético", yieldPerL: 12, match: ["Esmalte"], sector: "inmobiliario" },
  { key: "imperm", label: "Impermeabilizante", yieldPerL: 2, match: ["Impermeabilizante"], sector: "inmobiliario" },
  { key: "epoxi", label: "Epoxi para pisos", yieldPerL: 5, match: ["Epoxi"], sector: "industrial" },
];

const DOOR_M2 = 1.6, WINDOW_M2 = 1.2, MARGIN = 1.1;

export const computePaint = ({ mode, area, length, width, height, doors, windows, coats, yieldPerL }) => {
  let m2 = mode === "area" ? Number(area) || 0
    : Math.max(0, 2 * (Number(length) + Number(width)) * Number(height) - Number(doors) * DOOR_M2 - Number(windows) * WINDOW_M2);
  const liters = m2 > 0 ? Math.ceil((m2 * coats / yieldPerL) * MARGIN * 10) / 10 : 0;
  const buckets20 = Math.floor(liters / 20);
  const rest = liters - buckets20 * 20;
  const cans4 = Math.ceil(rest / 4);
  return { m2: Math.round(m2 * 10) / 10, liters, buckets20, cans4 };
};

const litersOf = (unit = "") => { const m = unit.match(/(\d+(?:[.,]\d+)?)\s*L/i); return m ? parseFloat(m[1].replace(",", ".")) : 0; };

export const PaintCalculator = ({ products = [] }) => {
  const [mode, setMode] = useState("room");
  const [f, setF] = useState({ area: 40, length: 4, width: 3, height: 2.6, doors: 1, windows: 1, coats: 2, type: "latex_int" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const type = PAINT_TYPES.find((t) => t.key === f.type);
  const r = useMemo(() => computePaint({ mode, ...f, yieldPerL: type.yieldPerL }), [mode, f, type]);

  const suggestions = useMemo(() => products
    .filter((p) => p.sector === type.sector && type.match.some((m) => (p.category || "").toLowerCase().includes(m.toLowerCase())))
    .sort((a, b) => litersOf(b.unit) - litersOf(a.unit)).slice(0, 2), [products, type]);

  const msg = `Hola ${STORE.name}, calculé que necesito aprox. ${r.liters} L de ${type.label.toLowerCase()} para ${r.m2} m² (${f.coats} ${f.coats === 1 ? "mano" : "manos"}). ¿Qué me recomiendan y cuánto sale?`;

  const num = (k, label, step = 0.1, min = 0, icon) => (
    <label className="block">
      <span className="text-[11px] font-bold tracking-widest uppercase text-gray-500 flex items-center gap-1">{icon}{label}</span>
      <input data-testid={`calc-${k}`} type="number" step={step} min={min} value={f[k]} onChange={(e) => set(k, e.target.value)}
        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1] text-sm bg-white" />
    </label>
  );

  return (
    <section id="section-calc" className="scroll-mt-20 py-16 bg-[#FFFBEB] border-y border-amber-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-2 text-[#B45309] font-bold text-xs tracking-widest uppercase mb-2">
          <Calculator className="w-4 h-4" /> Calculadora de pintura
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 max-w-2xl">
          ¿Cuántos litros necesitás?
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl">Ingresá las medidas del ambiente o los metros cuadrados y te decimos cuánta pintura comprar (incluye 10% de margen).</p>

        <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-6 items-start">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="inline-flex rounded-full border border-gray-300 p-1 bg-gray-50 mb-5" data-testid="calc-mode">
              {[["room", "Por ambiente"], ["area", "Por m²"]].map(([k, l]) => (
                <button key={k} data-testid={`calc-mode-${k}`} onClick={() => setMode(k)}
                  className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${mode === k ? "bg-[#0A3977] text-white" : "text-gray-600"}`}>{l}</button>
              ))}
            </div>

            {mode === "room" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {num("length", "Largo (m)", 0.1, 0, <Ruler className="w-3 h-3" />)}
                {num("width", "Ancho (m)", 0.1, 0, <Ruler className="w-3 h-3" />)}
                {num("height", "Alto (m)", 0.1, 0, <Ruler className="w-3 h-3" />)}
                {num("doors", "Puertas", 1, 0, <DoorOpen className="w-3 h-3" />)}
                {num("windows", "Ventanas", 1, 0, <AppWindow className="w-3 h-3" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">{num("area", "Superficie a pintar (m²)", 1, 0, <Ruler className="w-3 h-3" />)}</div>
            )}

            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] font-bold tracking-widest uppercase text-gray-500">Tipo de pintura</span>
                <select data-testid="calc-type" value={f.type} onChange={(e) => set("type", e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1] text-sm bg-white">
                  {PAINT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label} · {t.yieldPerL} m²/L por mano</option>)}
                </select>
              </label>
              <div>
                <span className="text-[11px] font-bold tracking-widest uppercase text-gray-500">Manos</span>
                <div className="mt-1 flex gap-2">
                  {[1, 2, 3].map((c) => (
                    <button key={c} data-testid={`calc-coats-${c}`} onClick={() => set("coats", c)}
                      className={`flex-1 py-2 rounded-lg border text-sm font-bold transition-colors ${f.coats === c ? "bg-[#0A3977] text-white border-[#0A3977]" : "bg-white border-gray-300 text-gray-700"}`}>{c}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#0A3977] text-white rounded-2xl p-6 shadow-xl" data-testid="calc-result">
            <div className="text-xs font-bold tracking-widest uppercase text-[#F59E0B]">Resultado</div>
            <div className="mt-3 flex items-end gap-2">
              <span data-testid="calc-liters" className="font-display font-black text-5xl leading-none">{r.liters}</span>
              <span className="text-blue-100 font-semibold pb-1">litros</span>
            </div>
            <div className="mt-2 text-sm text-blue-100">
              para <strong className="text-white" data-testid="calc-m2">{r.m2} m²</strong> · {f.coats} {f.coats === 1 ? "mano" : "manos"} de {type.label.toLowerCase()}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="bg-white/10 rounded-xl p-3">
                <div className="font-display font-bold text-2xl" data-testid="calc-buckets">{r.buckets20}</div>
                <div className="text-xs text-blue-100">baldes de 20 L</div>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <div className="font-display font-bold text-2xl" data-testid="calc-cans">{r.cans4}</div>
                <div className="text-xs text-blue-100">latas de 4 L</div>
              </div>
            </div>

            {suggestions.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-bold tracking-widest uppercase text-blue-200 mb-2">Te recomendamos</div>
                <div className="space-y-2">
                  {suggestions.map((p) => (
                    <Link key={p.id} to={productPageUrl(p)} data-testid={`calc-suggest-${p.code}`}
                      className="flex items-center justify-between gap-2 bg-white text-gray-900 rounded-xl px-3 py-2 hover:bg-blue-50 transition-colors">
                      <div className="min-w-0">
                        <div className="text-sm font-bold truncate">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.unit} · {formatGs(p.price)}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#0067B1] shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <a data-testid="calc-whatsapp" href={waLink(msg)} target="_blank" rel="noreferrer"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-bold hover:bg-[#1EBE5B] transition-colors">
              <MessageCircle className="w-4 h-4" /> Pedir asesoramiento
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
